/* One-click browser print view for a Purchase Order.
   Styled after the DV quotation template: navy angled header band,
   large document number with REV chip, BILL TO-style blocks and a
   navy item table. Opens the browser print dialog (preview + print/save). */

const COMPANY = {
  name: "D.V.Electromatic Pvt. Ltd.",
  tagline: "ELECTRICAL PANEL MANUFACTURERS",
  address: "Pathankot, Punjab, India",
  phone: "0186-2231231, 01870-25351",
  email: "dvepl@live.in",
};

const esc = (s) =>
  String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const nl2br = (s) => esc(s).replace(/\n/g, "<br/>");
const inr = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const two = (n) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? " " + ONES[n % 10] : ""}`);
const three = (n) => {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return `${h ? ONES[h] + " Hundred" + (rest ? " " : "") : ""}${rest ? two(rest) : ""}`;
};

export function amountInWords(amount) {
  let rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);
  let words = "Zero";
  if (rupees > 0) {
    const parts = [];
    const crore = Math.floor(rupees / 10000000); rupees %= 10000000;
    const lakh = Math.floor(rupees / 100000); rupees %= 100000;
    const thousand = Math.floor(rupees / 1000); rupees %= 1000;
    if (crore) parts.push(three(crore) + " Crore");
    if (lakh) parts.push(two(lakh) + " Lakh");
    if (thousand) parts.push(two(thousand) + " Thousand");
    if (rupees) parts.push(three(rupees));
    words = parts.join(" ");
  }
  return `Rupees ${words}${paise ? ` and ${two(paise)} Paise` : ""} Only`;
}

export function printPO(po, company, autoprint = true) {
  const CO = { ...COMPANY, ...(company || {}) };
  const items = (po.items || []).filter((it) => String(it.item || "").trim());
  let subtotal = 0;
  const rowsHtml = items.map((it, i) => {
    const qty = Number(it.qty) || 0;
    const rate = Number(it.rate) || 0;
    const dis = Number(it.dis) || 0;
    const net = rate * (1 - dis / 100);
    const total = net * qty;
    subtotal += total;
    return `<tr>
      <td class="c mono">${i + 1}</td>
      <td><div class="item-name">${esc(it.item)}</div>${it.specification ? `<div class="item-spec mono">${esc(it.specification)}</div>` : ""}</td>
      <td class="r mono">${qty}</td>
      <td class="mono">${esc(it.cat_no) || "—"}</td>
      <td class="r mono">₹${inr(rate)}</td>
      <td class="r mono">${dis || 0}%</td>
      <td class="r mono">₹${inr(net)}</td>
      <td class="r mono strong">₹${inr(total)}</td></tr>`;
  }).join("");
  const gstPct = Number(po.gst_percent) || 0;
  const gst = (subtotal * gstPct) / 100;
  const grand = subtotal + gst;
  const revs = (po.revisions || []).length;
  const lastRev = revs ? po.revisions[revs - 1] : null;
  const revMeta = lastRev
    ? `REV ${lastRev.rev_no} · ${lastRev.edited_by_initials || lastRev.edited_by || ""} · ${String(lastRev.edited_at || "").slice(0, 16).replace("T", " ")}`
    : "";
  const status = String(po.status || "draft").replace(/_/g, " ").toUpperCase();
  const contact = [po.concerned_person ? `Attn: ${po.concerned_person}` : "", po.contact_phone ? `Mob: ${po.contact_phone}` : "", po.contact_email]
    .filter(Boolean).join(" · ");
  const terms = (po.terms || []).map((t, i) => `<div class="term">${i + 1}. ${esc(t)}</div>`).join("");
  const logo = `${window.location.origin}/dvepl-logo.svg`;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(po.po_no)} — Purchase Order</title>
<link href="https://fonts.googleapis.com/css2?family=Manrope:wght@500;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet">
<style>
  * { margin:0; padding:0; box-sizing:border-box; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  :root { --navy:#0e7c3d; --ink:#1a2e22; --grey:#5b6b60; --line:#dde5da; --soft:#eef5f0; }
  body { font-family:'Manrope', 'Segoe UI', sans-serif; color:var(--ink); font-size:12px; }
  .mono { font-family:'JetBrains Mono', ui-monospace, monospace; }
  .page { max-width:860px; margin:0 auto; }
  /* ---- angled brand band ---- */
  .band { display:flex; border:1px solid var(--navy); }
  .band .brand { flex:1.4; background:#fff; padding:18px 22px; display:flex; align-items:center; gap:14px; }
  .band .brand img { width:52px; height:52px; }
  .band .brand .nm { font-size:22px; font-weight:800; letter-spacing:.3px; color:var(--navy); }
  .band .brand .tg { font-size:8px; letter-spacing:2.5px; color:var(--grey); margin-top:3px; font-weight:700; }
  .band .slant { flex:1; background:var(--navy); color:#fff; position:relative; display:flex; flex-direction:column;
                 justify-content:center; align-items:flex-end; padding:18px 22px;
                 clip-path:polygon(56px 0, 100% 0, 100% 100%, 0 100%); margin-left:-56px; }
  .band .slant .l1 { font-family:'JetBrains Mono', monospace; font-size:10px; letter-spacing:3px; line-height:1.9; text-align:right; }
  /* ---- document meta ---- */
  .docrow { display:flex; justify-content:space-between; align-items:flex-end; margin-top:26px; }
  .overline { font-family:'JetBrains Mono', monospace; font-size:9px; letter-spacing:3.5px; color:var(--grey); font-weight:500; }
  .docno { display:flex; align-items:center; gap:12px; margin-top:8px; }
  .docno .no { font-size:30px; font-weight:800; letter-spacing:.5px; color:var(--navy); }
  .rev-chip { font-family:'JetBrains Mono', monospace; font-size:10px; letter-spacing:2px; border:1.5px solid var(--navy);
              padding:5px 10px; color:var(--navy); font-weight:700; }
  .status { font-family:'JetBrains Mono', monospace; font-size:10px; letter-spacing:2.5px; color:var(--grey); margin-top:10px; }
  .issued { text-align:right; }
  .issued .val { font-size:16px; font-weight:800; color:var(--navy); margin-top:8px; }
  .rule { border-top:1px solid var(--line); margin:22px 0; }
  /* ---- parties ---- */
  .parties { display:flex; gap:32px; }
  .party { flex:1; }
  .party .who { font-size:19px; font-weight:800; color:var(--navy); margin-top:9px; }
  .party .ln { margin-top:5px; color:var(--ink); line-height:1.55; }
  .party .ln.mono { font-size:10.5px; color:var(--grey); }
  /* ---- items ---- */
  table { border-collapse:collapse; width:100%; margin-top:24px; }
  thead th { background:var(--navy); color:#fff; font-family:'JetBrains Mono', monospace; font-size:9px;
             letter-spacing:2.5px; font-weight:500; text-align:left; padding:11px 12px; }
  thead th.r { text-align:right; } thead th.c { text-align:center; }
  tbody td { padding:12px; border-bottom:1px solid var(--line); vertical-align:top; font-size:12px; }
  .item-name { font-weight:700; font-size:13px; }
  .item-spec { font-size:10px; color:var(--grey); margin-top:4px; letter-spacing:.5px; }
  .r { text-align:right; } .c { text-align:center; } .strong { font-weight:700; }
  /* ---- totals ---- */
  .tot { display:flex; justify-content:space-between; align-items:center; padding:11px 12px; border-bottom:1px solid var(--line); }
  .tot .lbl { font-family:'JetBrains Mono', monospace; font-size:10px; letter-spacing:3px; color:var(--grey); }
  .tot .amt { font-family:'JetBrains Mono', monospace; font-size:14px; font-weight:700; }
  .tot.grand { background:var(--navy); color:#fff; border:none; margin-top:2px; }
  .tot.grand .lbl { color:#cfe8d8; } .tot.grand .amt { font-size:17px; }
  .words { font-size:11px; color:var(--grey); font-style:italic; padding:10px 12px; background:var(--soft); margin-top:8px; }
  /* ---- terms + sign ---- */
  .sec-lab { font-family:'JetBrains Mono', monospace; font-size:9px; letter-spacing:3px; color:var(--grey); margin:26px 0 8px; }
  .term { font-size:10.5px; color:var(--ink); line-height:1.6; }
  .sig { margin-top:40px; display:flex; }
  .sig div { flex:1; border-top:1px solid var(--grey); padding-top:6px; margin-right:12px;
             font-family:'JetBrains Mono', monospace; font-size:8.5px; letter-spacing:1.5px; color:var(--grey); }
  .auth { margin-top:36px; text-align:right; }
  .auth .co { font-weight:800; color:var(--navy); }
  .auth .as { margin-top:46px; font-family:'JetBrains Mono', monospace; font-size:9px; letter-spacing:2px; color:var(--grey); }
  @page { size:A4; margin:10mm; }
  @media print { .page { max-width:none; } }
</style></head><body>
<div class="page">
  <div class="band">
    <div class="brand">
      <img src="${logo}" alt="logo"/>
      <div>
        <div class="nm">${esc(CO.name)}</div>
        <div class="tg">${esc(CO.tagline)}</div>
      </div>
    </div>
    <div class="slant">
      <div class="l1">${esc(CO.address).toUpperCase()}<br/>PH: ${esc(CO.phone)}<br/>${esc(CO.email).toUpperCase()}</div>
    </div>
  </div>

  <div class="docrow">
    <div>
      <div class="overline">PURCHASE ORDER</div>
      <div class="docno">
        <span class="no">${esc(po.po_no)}-R${revs}</span>
        ${revs ? `<span class="rev-chip">REV ${revs}</span>` : ""}
      </div>
      <div class="status">STATUS: ${esc(status)}</div>
      ${revMeta ? `<div class="status">${esc(revMeta)}</div>` : ""}
      ${po.created_by ? `<div class="status">MADE BY: ${esc(po.created_by_initials || po.created_by)}</div>` : ""}
    </div>
    <div class="issued">
      <div class="overline">ISSUED · DATE</div>
      <div class="val">${esc(po.po_date)}</div>
      ${po.ref ? `<div class="status">REF: ${esc(po.ref)}</div>` : ""}
    </div>
  </div>

  <div class="rule"></div>

  <div class="parties">
    <div class="party">
      <div class="overline">VENDOR · BILL TO</div>
      <div class="who">M/s ${esc(po.vendor_name)}</div>
      <div class="ln">${nl2br(po.vendor_address)}</div>
      ${contact ? `<div class="ln">${esc(contact)}</div>` : ""}
    </div>
    <div class="party">
      <div class="overline">SHIP TO · DELIVERY</div>
      <div class="ln" style="margin-top:9px">${nl2br(po.delivery_address)}</div>
      <div class="overline" style="margin-top:12px">CORRESPONDENCE</div>
      <div class="ln mono">${nl2br(po.corresponding_address)}</div>
    </div>
  </div>
  ${(po.required_date || po.po_validity || po.special_instructions) ? `<div class="words" style="margin-top:16px">
    ${[po.required_date ? `<b>REQUIRED BY:</b> ${esc(po.required_date)}` : "", po.po_validity ? `<b>PO VALID UPTO:</b> ${esc(po.po_validity)}` : ""].filter(Boolean).join(" &nbsp;&middot;&nbsp; ")}
    ${po.special_instructions ? `<br/><b>SPECIAL INSTRUCTIONS:</b> ${esc(po.special_instructions)}` : ""}
  </div>` : ""}

  <table>
    <thead><tr>
      <th class="c">#</th><th style="width:34%">ITEM · SPECIFICATION</th><th class="r">QTY</th>
      <th>CAT NO</th><th class="r">RATE</th><th class="r">DISC</th><th class="r">NET</th><th class="r">AMOUNT</th>
    </tr></thead>
    <tbody>${rowsHtml}</tbody>
  </table>
  <div class="tot"><span class="lbl">SUBTOTAL</span><span class="amt">₹${inr(subtotal)}</span></div>
  <div class="tot"><span class="lbl">GST @ ${gstPct}%</span><span class="amt">₹${inr(gst)}</span></div>
  <div class="tot grand"><span class="lbl">GRAND TOTAL</span><span class="amt">₹${inr(grand)}</span></div>
  <div class="words">${amountInWords(grand)}</div>

  <div class="sec-lab">TERMS &amp; CONDITIONS</div>
  ${terms}
  ${CO.gst_no ? `<div class="term" style="margin-top:6px;font-weight:700">GST NO: ${esc(CO.gst_no)}</div>` : ""}

  <div class="sig">
    <div>SIGNATURE</div><div>RECEIVING DATE</div><div>QTY RECEIVED</div><div>TEST CERTIFICATE REF.</div><div>QTY PASSED</div>
  </div>
  <div class="auth"><div class="co">For ${esc(CO.name)}</div><div class="as">AUTHORISED SIGNATORY</div></div>
</div>
${autoprint ? `<script>window.onload = function () { setTimeout(function () { window.print(); }, 350); };</script>` : ""}
</body></html>`;

  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}

// Full-screen read-only preview — same formal PO layout as printPO, opened in a new tab WITHOUT
// auto-triggering the browser print dialog (just for viewing the complete PO at a glance).
export function previewPO(po, company) {
  return printPO(po, company, false);
}
